import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { FadeUp, Overline } from "../components/Reveal";

const pillars = [
  {
    title: "Heritage",
    body: "We design as though we are adding to a family archive \u2014 pieces made to outlast the trend, to be worn in, handed down, and remembered. Time is our favourite material.",
  },
  {
    title: "Craft",
    body: "Everything is made by hand and finished slowly. Natural fabrics, honest construction, and a single detail in gold that reveals itself only to those who look twice.",
  },
  {
    title: "The Few",
    body: "We make little, and we make it for the few. Each piece is a quiet signal \u2014 recognised not by a logo, but by the way it carries you.",
  },
];

const About = () => {
  return (
    <div data-testid="about-page" className="min-h-screen pt-28 md:pt-36 pb-28">
      <div className="max-w-[900px] mx-auto px-6 md:px-12">
        <Link
          to="/"
          data-testid="about-back-link"
          className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-zinc-500 hover:text-gold transition-colors duration-300"
        >
          <ArrowLeft size={13} /> The House
        </Link>

        <FadeUp>
          <div className="mt-10">
            <Overline>About Us</Overline>
            <h1 className="mt-4 font-serif text-4xl md:text-6xl font-light text-[#F5F5F5] leading-tight">
              The House of <span className="italic text-gold">Vincen</span>
            </h1>
          </div>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="mt-10 space-y-6 text-sm md:text-base text-zinc-400 leading-relaxed">
            <p>
              Vincen is not a label. It is a way of living &mdash; a quiet confidence,
              worn close to the skin.
            </p>
            <p>
              We make pieces for people who have nothing to prove and everything to
              express. There is heritage in every seam and a story behind every thread.
              We do not follow seasons; we follow a <span className="text-gold">feeling</span>.
            </p>
            <p>
              Ours is a world of long afternoons and well-kept promises &mdash; of leather,
              linen and gold, of the roar of an engine and the hush of a cinema. We gather
              these worlds and tailor them into something you will keep for a lifetime,
              and one day pass on.
            </p>
            <p>
              Luxury, to us, is restraint: the finest things said softly. This is
              <span className="text-[#F5F5F5] italic"> Vincen</span> &mdash; welcome home.
            </p>
          </div>
        </FadeUp>

        <div className="mt-16 flex flex-col gap-10">
          {pillars.map((p, i) => (
            <FadeUp key={p.title} delay={0.05}>
              <div
                data-testid={`about-pillar-${i}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 border-t border-white/10 pt-8"
              >
                <div className="md:col-span-4">
                  <span className="font-serif text-5xl md:text-6xl font-light leading-none lux-numeral">
                    {["I", "II", "III"][i]}
                  </span>
                  <h2 className="mt-4 font-serif text-2xl md:text-3xl font-light text-[#F5F5F5]">
                    {p.title}
                  </h2>
                </div>
                <p className="md:col-span-8 text-sm md:text-base text-zinc-400 leading-relaxed">
                  {p.body}
                </p>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.1}>
          <div className="mt-16 border-t border-maroon/40 pt-12 text-center">
            <p className="font-serif italic text-2xl md:text-3xl text-zinc-500">Veni. Vidi.</p>
            <p className="mt-2 font-serif text-4xl md:text-5xl font-light text-gold">Vincen.</p>
            <Link
              to="/shop"
              data-testid="about-shop-link"
              className="mt-8 inline-flex items-center gap-3 px-10 py-4 bg-gold text-black text-xs tracking-[0.35em] uppercase font-semibold hover:bg-gold-hover transition-colors duration-500"
            >
              Explore the Collections <ArrowRight size={14} />
            </Link>
          </div>
        </FadeUp>
      </div>
    </div>
  );
};

export default About;
